/**
 * Date-range presets for the tenant credits page. Pure + framework-free so the
 * ledger-query bounds are unit-tested. No money here — this only resolves a
 * preset to inclusive `from`/`to` local-calendar days.
 */

export type TDateRange = "mtd" | "7d" | "30d" | "90d"

/**
 * Local-calendar `YYYY-MM-DD`. Deliberately NOT `Date#toISOString().slice(0,10)`:
 * that converts to UTC, so in a timezone ahead of UTC (e.g. Europe/Amsterdam) a
 * local-constructed midnight rolls back to the previous day — which pulled the
 * prior day's (and, at a month boundary, the prior month's) spend into the range.
 */
function localYmd(d: Date): string {
  const y = String(d.getFullYear())
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return [y, m, day].join("-")
}

const DAY_MS = 86_400_000

/**
 * Resolve a {@link TDateRange} preset to inclusive `from`/`to` ledger-query
 * bounds, expressed in the USER's local calendar days. `now` is injectable for
 * testing.
 */
export function getDateRange(range: TDateRange, now: Date = new Date()): { from: string; to: string } {
  const to = localYmd(now)
  let from: string
  switch (range) {
    case "mtd":
      from = localYmd(new Date(now.getFullYear(), now.getMonth(), 1))
      break
    case "7d":
      from = localYmd(new Date(now.getTime() - 7 * DAY_MS))
      break
    case "30d":
      from = localYmd(new Date(now.getTime() - 30 * DAY_MS))
      break
    case "90d":
      from = localYmd(new Date(now.getTime() - 90 * DAY_MS))
      break
  }
  return { from, to }
}
